import {Api} from "laravel-request";
import { useEffect, useMemo, useRef, useState } from 'react';

import {UserDefaultConfigConstants} from "#app/contexts/UserDefaultConfigConstants";

type rawConfigData = { id?: number, key: string, value: any }[]

const EMPTY_CONFIG: rawConfigData = [];

function buildDefaultConfig(): rawConfigData {
    return Object.keys(UserDefaultConfigConstants).map((key) => ({
        key,
        value: Object(UserDefaultConfigConstants)[key],
    }));
}

export const useUserConfig = (
    initialConfig: rawConfigData = EMPTY_CONFIG,
    callback?: () => void
) => {
    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    const [config, setConfigState] = useState<rawConfigData>([]);
    const [refresh, setRefresh] = useState(false);
    const initialConfigRef = useRef(initialConfig);
    initialConfigRef.current = initialConfig;
    const initialConfigSnapshot = JSON.stringify(initialConfig);

    useEffect(() => {
        const cfg = initialConfigRef.current;
        if (cfg.length > 0) {
            setConfigState(cfg);
        } else {
            setConfigState(buildDefaultConfig());
        }
        setRefresh(true);
        setIsLoaded(true);
    }, [initialConfigSnapshot]);

    useEffect(() => {
        if (refresh)
            setRefresh(false)
    }, [refresh]);

    /**
     *
     * @param key
     */
    const getConfigByKeyJson = (key: keyof typeof UserDefaultConfigConstants) =>
    {
        const value = getConfByKey(key);
        if(value && typeof value === 'string') return JSON.parse(value);
    }

    const getConfByKey = <K extends keyof typeof UserDefaultConfigConstants>(
        key: K
    ): (typeof UserDefaultConfigConstants)[K] => {

        const conf = config.filter((conf) => {
            return conf.key === key
        })

        if (conf.length > 0) {

            const dbValue = conf[0].value

            let normalizedData: string | number | null


            switch (typeof UserDefaultConfigConstants[key]) {
                case "object":
                    normalizedData = (typeof dbValue === 'string' ? JSON.parse(String(dbValue)) : dbValue)
                    break
                case "number":
                    normalizedData = parseInt(dbValue)
                    break
                case "string":
                    normalizedData = String(dbValue)
                    break
                default:
                    normalizedData = dbValue
            }

            return normalizedData as (typeof UserDefaultConfigConstants)[K]
        }

        return UserDefaultConfigConstants[key]
    }

    /**
     * 
     * @param key
     * @param newValue
     * @param savedCallback
     */
    const setConfig = (
        key: keyof typeof UserDefaultConfigConstants,
        newValue: typeof UserDefaultConfigConstants,
        savedCallback?: (res: any) => void
    ) => {
        const configFiltered = config.filter((conf) => {
            return conf.key === key
        });

        let valueToSave: string
        switch (typeof newValue) {
            case "object":
                valueToSave = JSON.stringify(newValue)
                break
            default:
                valueToSave = String(newValue)
        }

        const data = {
            key: key,
            value: valueToSave,
        }

        if (configFiltered.length > 0 && configFiltered[0].id)
        {
            const _updateConfigRequest = Api.putArg('config', 'update', configFiltered[0].id, data)
                .withoutNotify()
                .call((response) => {
                    const responseRecord = {
                        id: response.meta.id,
                        key: key,
                        value: newValue
                    }
                    const newValues = config.filter(
                        (configEntry) => configEntry.id !== responseRecord.id
                    );
                    setConfigState(newValues.concat([responseRecord]));

                    savedCallback?.(response);
                    callback?.();
                })
        } else {

            const _setConfigRequest = Api.post('config', 'store', data).withoutNotify()
                .call((res) => {
                    const rec = {
                        id: res.meta.id,
                        key: key,
                        value: newValue
                    }
                    const newValues = config.filter((i) => i.key !== rec.key)
                    setConfigState(newValues.concat([rec]))

                    savedCallback?.(res);
                    callback?.();
                })
        }

    }

    /**
     *
     * @param keyPairValues
     */
    const setConfigBulk = (keyPairValues: {
        name: keyof typeof UserDefaultConfigConstants,
        value: any }[]) => {
        keyPairValues.map((el) => {
            setConfig(el.name, el.value)
        })
    }

    return useMemo(
        () => ({
            refresh,
            config,
            isLoaded,
            getConfByKey,
            getConfByKeyJson: getConfigByKeyJson,
            setConfig,
            setConfigBulk,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- helpers close over `config`
        [refresh, config, isLoaded]
    );
}
